'use client'

import React from 'react'
import {useTranslations} from 'next-intl'
import {css, useTheme} from '@emotion/react'
import {Paper, Tooltip} from '@mui/material'
import * as materialColors from '@mui/material/colors'

import ControlPanel from './ControlPanel'
import ControlDetailPanelLayout from './ControlDetailPanelLayout'
import Wheel from './Wheel'
import {useAppCtx} from '../../../../contexts/appCtx'
import useFullScreenFeature from '../../../../hooks/useFullScreenFeature'

const OptionCacheKey = 'draw_lots_options'

export default function DrawLots() {
  const theme = useTheme()
  const t = useTranslations()

  const DefaultOptions = [
    {
      id: 'default-1',
      weight: 1,
      color: materialColors.amber[200],
      name: t('draw-lots.option') + ` 1`,
    },
    {
      id: 'default-2',
      weight: 1,
      color: materialColors.blue[200],
      name: t('draw-lots.option') + ` 2`,
    },
    {
      id: 'default-3',
      weight: 1,
      color: materialColors.brown[200],
      name: t('draw-lots.option') + ` 3`,
    }
  ]

  const [options, setOptions] = React.useState([...DefaultOptions])
  const [initialized, setInitialized] = React.useState(false)
  React.useEffect(() => {
    const value = localStorage.getItem(OptionCacheKey)
    if (value) {
      try {
        const options = JSON.parse(value)
        setOptions(options)
      } catch (e) {
        localStorage.removeItem(OptionCacheKey)
      }
    }

    setInitialized(true)
  }, [setOptions, setInitialized])

  React.useEffect(() => {
    if (initialized && options.length > 0) {
      localStorage.setItem(OptionCacheKey, JSON.stringify(options))
    }
  }, [initialized, options])

  const [currentSelection, setCurrentSelection] = React.useState(null)
  React.useEffect(() => {
    setCurrentSelection(null)
  }, [options])

  const onChange = React.useCallback((currentSelection) => {
    setCurrentSelection(currentSelection)
  }, [setCurrentSelection])

  const [spinning, setSpinning] = React.useState(false)
  const onStart = React.useCallback(() => {
    setSpinning(true)
  }, [setSpinning])

  const onFinished = React.useCallback(() => {
    setSpinning(false)
  }, [setSpinning])

  const appCtx = useAppCtx()
  const [ResultFullScreenButton, ResultFullScreenContainer] = useFullScreenFeature()

  const [overrideActiveTab, setOverrideActiveTab] = React.useState(null)

  return (
    <ControlDetailPanelLayout
      overrideActiveTab={overrideActiveTab}

      controlLabel={t('draw-lots.options')}

      control={
        <ControlPanel
          readOnly={spinning}
          defaultOptions={DefaultOptions}
          options={options}
          onOptionsChange={setOptions}
          setOverrideActiveTab={setOverrideActiveTab}
        />
      }

      detailLabel={t('draw-lots.wheel')}
    >
      <Paper css={css`
        height: 100%;
        background: ${theme.palette.surface.main};

        padding: 24px;

        display: flex;
        flex-direction: column;
        justify-content: center;

        @media (max-width: ${theme.breakpoints.sm}px) {
          padding: 8px;
        }
      `}>
        {!appCtx.isMobile && <Paper
          css={css`
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;

            display: flex;
            justify-content: flex-end;
          `}
        >
          <div css={css`
            padding: 4px 16px;
          `}>
            <ResultFullScreenButton/>
          </div>
        </Paper>}
        <Paper css={css`
          border-top-left-radius: 0;
          border-top-right-radius: 0;

          flex: 1;
          padding: 16px;

          @media (max-width: ${theme.breakpoints.sm}px) {
            padding: 8px;
          }
        `}>
          <ResultFullScreenContainer>
            <div css={css`
              height: 100%;
              display: flex;
              flex-direction: column;
            `}>
              <div css={css`
                flex: 1;
              `}>
                {options.length > 0 ? (
                  <Wheel
                    selections={options}
                    onStart={onStart}
                    onChange={onChange}
                    onFinished={onFinished}
                    primaryColor={theme.palette.primary.dark}
                    contrastColor={theme.palette.primary.lightest}
                    buttonText={t('draw-lots.spin')}
                  />) : null}
              </div>

              <div css={css`
                flex: 0 0 100px;
                align-self: center;

                display: flex;
                align-items: center;
              `}>
                {currentSelection ? (
                  <Paper
                    css={css`
                      min-width: 240px;
                      padding: 16px;

                      display: flex;
                      align-items: center;
                      gap: 16px;

                      @media (max-width: ${theme.breakpoints.sm}px) {
                        padding: 8px;
                      }
                    `}
                  >
                    <Paper
                      css={css`
                        min-width: 48px;
                        min-height: 48px;

                        background: ${currentSelection.color};

                        @media (max-width: ${theme.breakpoints.sm}px) {
                          min-width: 40px;
                          min-height: 40px;
                        }
                      `}
                    />
                    <Tooltip title={currentSelection.name}>
                      <div css={css`
                        font-size: 1.6em;
                        font-weight: 800;
                        line-height: 48px;

                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;

                        @media (max-width: ${theme.breakpoints.sm}px) {
                          font-size: 1.4em;
                        }
                      `}>{currentSelection.name}</div>
                    </Tooltip>
                  </Paper>
                ) : (
                  <div></div>
                )}
              </div>

            </div>
          </ResultFullScreenContainer>
        </Paper>
      </Paper>
    </ControlDetailPanelLayout>
  )
}

